import { ReactElement, useState } from "react";
import { Switch, Route } from "_/components/router";
import { t } from "@lingui/core/macro";
import { useAtom } from "jotai";

import { routes } from "_/routes";
import { projectsLayoutAtom, type ProjectsLayout } from "_/state";

import { Button, ButtonGroup } from "_/components/button";
import { Modal } from "_/components/modal";
import { ProjectDetail } from "_/components/project-detail";
import { Icon } from "_/components/icon";
import { H5 } from "_/components/typography";
import { ProjectCreationForm } from "_/components/project-forms";
import { ProjectCards } from "_/components/project-card";
import { ProjectsTable } from "_/components/projects-table";
import { PermissionTest } from "_/components/permission-test";
import { permissions } from "_/data/rbac/definitions";

import * as S from "./styled";

const LayoutButton = ({
  layout,
  current,
  title,
  onSelect,
}: {
  layout: ProjectsLayout;
  current: ProjectsLayout;
  title: string;
  onSelect: (layout: ProjectsLayout) => void;
}) => {
  const isActive = layout === current;
  const icon = layout === "card" ? "GridView" : "ViewHeadline";

  return (
    <Button
      kind="secondary"
      size="small"
      variant={isActive ? "contained" : "hollow"}
      iconOnly
      title={title}
      aria-label={title}
      aria-pressed={isActive}
      onClick={() => onSelect(layout)}
    >
      <Icon variant={icon} />
    </Button>
  );
};

/**
 * View for displaying listing of projects, as cards or as a table.
 *
 * @category components
 */
export const ProjectsView = (): ReactElement => {
  const [showModal, setShowModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [layout, setLayout] = useAtom(projectsLayoutAtom);

  function openModal() {
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setIsUploading(false);
  }

  return (
    <Switch>
      <Route path={routes.project.wildcards.index}>
        {(p) => <ProjectDetail projectId={p.projectId} />}
      </Route>
      <Route path={routes.projects.index}>
        <S.Wrapper>
          <S.SubHeader>
            <H5>{t`common.projects`}</H5>
            <S.Actions>
              <ButtonGroup
                role="group"
                aria-label={t`components.project-view.layout`}
              >
                <LayoutButton
                  layout="card"
                  current={layout}
                  title={t`components.project-view.card-layout`}
                  onSelect={setLayout}
                />
                <LayoutButton
                  layout="list"
                  current={layout}
                  title={t`components.project-view.list-layout`}
                  onSelect={setLayout}
                />
              </ButtonGroup>
              <PermissionTest requiredPermissions={permissions.projects.write}>
                <Button onClick={openModal}>
                  <Icon variant="Add" />
                  {t`common.create`}
                </Button>
              </PermissionTest>
            </S.Actions>
          </S.SubHeader>
          {layout === "card" ? (
            <ProjectCards showArchived layout="grid" />
          ) : (
            <ProjectsTable showArchived />
          )}
          <Modal
            title={t`components.project-view.create-new`}
            open={showModal}
            onClose={isUploading ? undefined : closeModal}
          >
            <ProjectCreationForm
              onComplete={closeModal}
              isUploading={isUploading}
              setIsUploading={setIsUploading}
            />
          </Modal>
        </S.Wrapper>
      </Route>
    </Switch>
  );
};
